import { Injectable, inject } from '@angular/core';
import { ReplaySubject, Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { ArraySort, HeaderSort } from '@shared';

import { Patient } from '../../models/patient.model';
import { ProblemInstanceService } from '../problem-instance/problem-instance.service';
import { AddressFinderService } from '../address-finder/address-finder.service';
import { TaskFeasibility } from '../../models/task-feasibility.model';
import { TaskUser } from '../../models/task-user.model';
import { PatientTaskService } from '../patient-task/patient-task.service';

@Injectable({
    providedIn: 'root'
})
export class PatientService {
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly addressFinderService = inject(AddressFinderService);
    private readonly patientTaskService = inject(PatientTaskService);

    private readonly patients$: ReplaySubject<Patient[]>;

    constructor() {
        this.patients$ = new ReplaySubject(1);
        this.getPatientsFromInstance().subscribe(this.patients$);
    }

    public getPatients(
        sortValue?: HeaderSort,
        searchPhrase?: string,
        selectFirst?: boolean
    ): Observable<Patient[]> {
        return this.patients$.asObservable().pipe(
            map(patients => {
                const existSelectedPatient = patients.some(patient => patient.selected);

                if (searchPhrase) {
                    patients = this.filterPatients(searchPhrase, patients);
                }

                if (sortValue) {
                    this.sortPatients(patients, sortValue);
                }

                if (selectFirst && !existSelectedPatient && patients.length > 0) {
                    patients[0].selected = true;
                }

                return patients;
            })
        );
    }

    public ExistInvalidPatientAddress(): Observable<boolean> {
        return this.patients$.pipe(map(patients => patients.some(patient => !patient.addressValid)));
    }

    public filterPatients(searchPhrase: string, patients: Patient[]): Patient[] {
        return patients.filter(
            patient =>
                (patient.patientId && patient.patientId.toString().toLowerCase().includes(searchPhrase)) ||
                (patient.patientName && patient.patientName.toLowerCase().includes(searchPhrase)) ||
                (patient.streetAddressNameNumberAndLetter &&
                    patient.streetAddressNameNumberAndLetter.toLowerCase().includes(searchPhrase))
        );
    }

    public reloadPatientFeasibility(patient: Patient): Observable<void> {
        this.patientTaskService.initPatientTasksFeasibility();
        return this.problemInstanceService.getProblemInstance().pipe(
            switchMap(result => this.mapFeasibility([patient], result.problemInstance.tasks)),
            map(() => { })
        );
    }

    public reloadPatientsFeasibility(): Observable<void> {
        this.patientTaskService.initPatientTasksFeasibility();
        return this.problemInstanceService.getProblemInstance().pipe(
            switchMap(result =>
                this.getPatients().pipe(
                    switchMap(patients => this.mapFeasibility(patients, result.problemInstance.tasks))
                )
            ),
            map(() => { })
        );
    }

    public sortPatients(patients: Patient[], sortValue: HeaderSort): void {
        if (sortValue.sortBy === 'status') {
            ArraySort.sort(
                patients,
                'zipCodeWarning',
                sortValue.sortAscending,
                'feasibilityWarning',
                sortValue.sortAscending
            );
            ArraySort.sort(
                patients,
                'addressValid',
                !sortValue.sortAscending,
                'feasibilityError',
                sortValue.sortAscending
            );
            return;
        }

        ArraySort.sort(
            patients,
            sortValue.sortBy,
            sortValue.sortAscending,
            sortValue.secondarySortBy,
            sortValue.secondarySortAscending
        );
    }

    public getPatientFromInstance(patientId: string): Observable<Patient> {
        return this.problemInstanceService
            .getProblemInstance()
            .pipe(map(result => result.problemInstance.patients.find(i => i.patientId === patientId)));
    }

    public deselectPatient(): void {
        this.getPatients()
            .pipe(
                tap(patients => {
                    const selectedPatient = patients?.find(patient => patient.selected);
                    if (selectedPatient) {
                        selectedPatient.selected = false;
                    }
                })
            )
            .subscribe();
    }

    private getPatientsFromInstance(): Observable<Patient[]> {
        return this.problemInstanceService.getProblemInstance().pipe(
            switchMap(result =>
                this.addressFinderService
                    .validateAddresses(result.problemInstance.patients, result.problemInstance.officeInfo)
                    .pipe(
                        map(validateResult =>
                            result.problemInstance.patients.map(patient => {
                                const patientToAddressStatus = validateResult.patientToAddressStatusList.find(
                                    i => i.patientId === patient.patientId
                                );
                                return <Patient>{
                                    ...patient,
                                    zipCodeSuggestion: patientToAddressStatus?.zipCodeSuggestion,
                                    zipCodeWarning: patientToAddressStatus
                                        ? patientToAddressStatus.zipCodeWarning
                                        : false,
                                    addressValid: patientToAddressStatus ? patientToAddressStatus.status : false
                                };
                            })
                        ),
                        switchMap(patients => this.mapFeasibility(patients, result.problemInstance.tasks))
                    )
            )
        );
    }

    private mapFeasibility(patients: Patient[], tasks: TaskUser[]): Observable<Patient[]> {
        return this.patientTaskService.getPatientTasksFeasibility().pipe(
            map(taskFeasibility => {
                patients.forEach(patient => {
                    const patientTaskIds = tasks
                        .filter(task => task.patientId === patient.patientId)
                        .map(task => task.originalTaskId);

                    patient.tasksFeasibilityErrorCount = 0;
                    patient.tasksFeasibilityWarningCount = 0;

                    patientTaskIds.forEach(id => this.countPatientTaskFeasibility(patient, id, taskFeasibility));

                    patient.feasibilityError = patient.tasksFeasibilityErrorCount > 0;
                    patient.feasibilityWarning = patient.tasksFeasibilityWarningCount > 0;
                });

                return patients;
            })
        );
    }

    private countPatientTaskFeasibility(patient: Patient, taskId: string, taskFeasibility: TaskFeasibility): void {
        const taskHasError = Object.values(taskFeasibility.errors).some(items =>
            items.some(item => item.taskId === taskId)
        );
        patient.tasksFeasibilityErrorCount += taskHasError ? 1 : 0;

        const taskHasWarning = Object.values(taskFeasibility.warnings).some(items =>
            items.some(item => item.taskId === taskId)
        );
        patient.tasksFeasibilityWarningCount += taskHasWarning ? 1 : 0;
    }
}
